import deepEqual from "fast-deep-equal";
import findIndex from "ramda/src/findIndex";
import { useEffect, useRef, useState } from "react";
import { findClosestValue } from "./find_closest_value";

export type Range = [from: number, to: number];

export type SliderProps = {
  min: number;
  max: number;
  onAfterChange: (newValue: Range) => void;
  onChange: (newValue: Range) => void;
  value: Range;
};

export type RangeValue = [number, string];

const findValueIndex = (rangeValues: number[], value: number) =>
  findIndex<number>((element) => element === value)(rangeValues);

const findValueIndices = (
  rangeValues: number[],
  value: Range | undefined
): [number, number] => {
  if (value === undefined) {
    return [0, rangeValues.length - 1];
  } else {
    const fromIndex = findValueIndex(rangeValues, value[0]);
    const toIndex = findValueIndex(rangeValues, value[1]);
    return [
      fromIndex === -1
        ? findClosestValue(value[0], rangeValues) ?? 0
        : fromIndex,
      toIndex === -1
        ? findClosestValue(value[1], rangeValues) ?? rangeValues.length - 1
        : toIndex,
    ];
  }
};

export const useSliderRange = (
  range: RangeValue[],
  value: Range | undefined,
  onChange: (range: Range) => void
): {
  sliderProps: SliderProps;
  values: { from: RangeValue; to: RangeValue };
} => {
  const manuallySelectedValues = useRef<[number | null, number | null]>([
    null,
    null,
  ]);

  const [inProgressIndex, setInProgressIndex] = useState<[number, number]>(
    () => {
      const sortedRangeValues = (range ?? []).map((value) => value[0]);
      return findValueIndices(sortedRangeValues, value);
    }
  );

  const handleChange = (newValue: [number, number]) => {
    manuallySelectedValues.current = newValue;
    setInProgressIndex(newValue);
  };

  useEffect(() => {
    const sortedRangeValues = (range ?? []).map((value) => value[0]);
    if (value !== undefined) {
      // We already filtered by this parameter
      // First, let's grab all the values of the new range

      // Now, let's find the closest values in the new range to the values selected
      // in the previous range. E.g if in the old filter we selected 50.5 <= X >= 60
      // and the new values are [50, 52, ..., 57, 62, 63, ...]
      // then the new filter is  50 <= X >= 62
      setInProgressIndex([
        findClosestValue(value[0], sortedRangeValues) ?? 0,
        findClosestValue(value[1], sortedRangeValues) ?? range.length - 1,
      ]);
    }
  }, [range]);

  const previousValue = useRef(value);
  useEffect(() => {
    if (value === undefined) {
      // Either, active value was removed, or range changed and user haven't touched this range yet.
      setInProgressIndex([0, range.length - 1]);
    } else if (!deepEqual(value, previousValue.current)) {
      // Value changed, let's find its indices
      previousValue.current = value;
      const sortedRangeValues = range.map((value) => value[0]);
      setInProgressIndex(findValueIndices(sortedRangeValues, value));
    }
  }, [value, range]);

  const currentFromValue = range[inProgressIndex[0]] ?? range[0];
  const currentToValue = range[inProgressIndex[1]] ?? range[range.length - 1];

  const handleAfterChange = ([fromIndex, toIndex]: Range) => {
    onChange([range[fromIndex][0], range[toIndex][0]]);
  };

  return {
    values: { from: currentFromValue, to: currentToValue },
    sliderProps: {
      min: 0,
      max: range.length - 1,
      value: inProgressIndex,
      onChange: handleChange,
      onAfterChange: handleAfterChange,
    },
  };
};
